import { useLocalSearchParams, usePathname } from 'expo-router';
import type { Href } from 'expo-router';
import {
  ContactIcon,
  HomeIcon,
  MegaphoneIcon,
  SettingsIcon,
  ShieldAlertIcon,
  UserCircleIcon,
} from 'lucide-react-native';

import { DashboardSidebar, type SidebarItem } from '@/components/dashboard-sidebar';
import { useTenant } from '@/components/tenant-provider';
import { Text } from '@sixam/ui/text';
import { useReleaseFeatureFlag } from '@/lib/use-release-feature-flag';

/** Admin dashboard sidebar (web only). */
export function AdminSidebar() {
  const pathname = usePathname();
  const { tenant } = useLocalSearchParams<{ tenant: string }>();
  const { activeTenant } = useTenant();
  const contactManagementEnabled = useReleaseFeatureFlag('contactManagement') === true;
  const broadcastMessagingEnabled = useReleaseFeatureFlag('broadcastMessaging') === true;
  const integrationSettingsEnabled = useReleaseFeatureFlag('integrationSettings') === true;

  const base = `/admin/${tenant}`;
  const items: SidebarItem[] = [
    {
      label: 'Profile',
      icon: UserCircleIcon,
      href: { pathname: '/admin/[tenant]', params: { tenant } },
      active: pathname === base || pathname.startsWith(`${base}/profile`),
    },
    {
      label: 'Home',
      icon: HomeIcon,
      href: '/app/home',
      active: pathname.startsWith('/app/home'),
    },
    {
      label: 'Reports',
      icon: ShieldAlertIcon,
      href: '/admin/moderation',
      active: pathname.startsWith('/admin/moderation'),
    },
  ];
  if (contactManagementEnabled) {
    items.push({
      label: 'Contacts',
      icon: ContactIcon,
      href: { pathname: '/(authenticated)/admin/[tenant]/contacts', params: { tenant } },
      active: pathname.startsWith(`${base}/contacts`),
    });
  }
  if (broadcastMessagingEnabled) {
    items.push({
      label: 'Broadcasts',
      icon: MegaphoneIcon,
      href: {
        pathname: '/(authenticated)/admin/[tenant]/broadcasts',
        params: { tenant },
      } as unknown as Href,
      active: pathname.startsWith(`${base}/broadcasts`),
    });
  }
  if (integrationSettingsEnabled) {
    items.push({
      label: 'Settings',
      icon: SettingsIcon,
      href: `/admin/${tenant}/settings/integrations` as Href,
      active: pathname.startsWith(`${base}/settings`),
    });
  }

  return (
    <DashboardSidebar
      brand={activeTenant?.name ?? tenant ?? '6am'}
      subtitle="Admin"
      items={items}
      footer={
        <Text variant="muted" className="flex-1 text-xs" numberOfLines={1}>
          {activeTenant?.slug ?? tenant}
        </Text>
      }
    />
  );
}
